import { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Spin, message, Row, Col, Select, Space } from 'antd';
import { FilePdfOutlined, PaperClipOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import PayStubModal from './PayStubModal';
import withRoleAccess from '../../hoc/withRoleAccess';
import { PDFDocument } from 'pdf-lib'; 
import { useTranslation } from 'react-i18next';
import './Teacher.css';

const { Option } = Select;

const TeacherPaymentReport = () => {
    const { t } = useTranslation();
    const [paymentReports, setPaymentReports] = useState([]);
    const [filteredReports, setFilteredReports] = useState([]);
    const [selectedPayStub, setSelectedPayStub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [teachers, setTeachers] = useState([]);
    const [periods, setPeriods] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        // Extract unique teachers and periods from payment reports
        if (paymentReports.length > 0) {
            const uniqueTeachers = [...new Set(paymentReports.map(report => report.teacher_name))]
                .filter(Boolean)
                .sort();
            setTeachers(uniqueTeachers);

            const uniquePeriods = [...new Set(paymentReports.map(report => report.period))]
                .filter(Boolean)
                .sort((a, b) => new Date(b) - new Date(a)); // Sort by date, newest first
            setPeriods(uniquePeriods);
        }
    }, [paymentReports]);

    const fetchPaymentReports = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/teacher-hours/teacher-payment-report`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPaymentReports(response.data);
            setFilteredReports(response.data);
        } catch (error) {
            console.error(t('errorFetchingPaymentReports'), error);

            if (error.response && error.response.status === 404) {
                setPaymentReports([]);
                setFilteredReports([]);
                message.info(t('noPaymentReportsFound'));
            } else {
                message.error(t('failedToFetchTeachers'));
            }
        } finally {
            setLoading(false);
        }
    }, [API_URL, t]);
      
    useEffect(() => {
        fetchPaymentReports();
    }, [fetchPaymentReports]);

    const handleDelete = async (batchId) => {
        Modal.confirm({
            title: t('confirmDelete'),
            content: t('areYouSureDelete'),
            okText: t('Yes'),
            cancelText: t('No'),
            onOk: async () => {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_URL}/teacher-hours/teacher-payment-report/${batchId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    message.success(t('deleteSuccess'));
                    await fetchPaymentReports();  
                } catch (error) {
                    console.error('Error deleting batch:', error);
                    message.error(t('deleteFailed'));
                }
            }
        });
    };
      
    const filterReports = useCallback(() => {
        let filtered = [...paymentReports];
      
        if (selectedTeacher) {
            filtered = filtered.filter(report => report.teacher_name === selectedTeacher);
        }
      
        if (selectedPeriod) {
            filtered = filtered.filter(report => report.period === selectedPeriod);
        }
      
        setFilteredReports(filtered);
    }, [paymentReports, selectedTeacher, selectedPeriod]);

    useEffect(() => {
        filterReports();
    }, [filterReports]);

    const handleReset = () => {
        setSelectedTeacher(null);
        setSelectedPeriod(null);
    };

    const formatTotalAmount = (amount) => {
        return typeof amount === 'string' && !isNaN(parseFloat(amount))
            ? parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : parseFloat(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDateToDMY = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
    };

    const formatPeriod = (periodString) => {
        if (!periodString) return '';
        
        // Handle YYYY-MM format
        if (periodString.match(/^\d{4}-\d{2}$/)) {
            const [year, month] = periodString.split('-');
            const date = new Date(parseInt(year), parseInt(month) - 1);
            return date.toLocaleString('default', { month: 'short', year: '2-digit' });
        }
        
        // Handle date range format if still exists
        if (periodString.includes('_')) {
            const [startDateStr, endDateStr] = periodString.split('_');
            const startParts = startDateStr.split('-').map(Number);
            const endParts = endDateStr.split('-').map(Number);
            
            const startDate = new Date(startParts[0], startParts[1] - 1, startParts[2]);
            const endDate = new Date(endParts[0], endParts[1] - 1, endParts[2]);
            
            const startYear = startDate.getFullYear().toString().slice(-2);
            const endYear = endDate.getFullYear().toString().slice(-2);
            
            const startFormatted = startDate.getDate() + ' ' + 
                                  startDate.toLocaleString('default', { month: 'short' }) + ' ' + 
                                  startYear;
            
            const endFormatted = endDate.getDate() + ' ' + 
                                endDate.toLocaleString('default', { month: 'short' }) + ' ' + 
                                endYear;
            
            return `${startFormatted} - ${endFormatted}`;
        }
        
        return periodString;
    };

    const numberToWords = (num) => {
        const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
        const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
        const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    
        if (num === 0) return "Zero";
    
        let words = "";
    
        if (Math.floor(num / 1000) > 0) {
            words += numberToWords(Math.floor(num / 1000)) + " Thousand ";
            num %= 1000;
        }
    
        if (Math.floor(num / 100) > 0) {
            words += numberToWords(Math.floor(num / 100)) + " Hundred ";
            num %= 100;
        }
    
        if (num >= 10 && num < 20) {
            words += teens[num - 10] + " ";
        } else if (num >= 20) {
            words += tens[Math.floor(num / 10)] + " ";
            num %= 10;
        }
    
        if (num > 0) {
            words += ones[num] + " ";
        }
    
        return words.trim();
    };
    
    const numberToWordsWithBahtAndSatang = (amount) => {
        const numericAmount = parseFloat(amount);
        
        if (isNaN(numericAmount)) {
            throw new Error("Invalid amount: amount must be a number");
        }
    
        const [bahtPart, satangPart] = numericAmount.toFixed(2).split('.').map(Number);
        
        let words = numberToWords(bahtPart) + " Baht";
        
        if (satangPart > 0) {
            words += " and " + numberToWords(satangPart) + " Satang";
        }
    
        return words;
    };
    
    function formatNationalId(id) {
        if (!id) return '';
        
        const cleanId = id.replace(/\D/g, '');
        
        if (cleanId.length !== 13) {
            return cleanId;
        }
        
        return `${cleanId.substring(0, 1)} ${cleanId.substring(1, 5)} ${cleanId.substring(5, 10)} ${cleanId.substring(10, 12)} ${cleanId.substring(12, 13)}`;
    }

    const generateTawi50PDF = async (report) => {
        try {
            const existingPdfBytes = await fetch('/images/tawi50.pdf').then(res =>
                res.arrayBuffer()
            );
    
            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const form = pdfDoc.getForm();
    
            form.getTextField('name1').setText('KEAES Academics');
            form.getTextField('add1').setText('982/22 Gateway Ekkamai Fl.4 Sukhumvit Road, Phra Kanong Khlong Toey, Bangkok, 10110');
            form.getTextField('id1').setText('0 0000 00000 00 0');
    
            form.getTextField('name2').setText(report.teacher_name || '');
            form.getTextField('add2').setText(report.address || '');
            form.getTextField('id1_2').setText(formatNationalId(report.nationalid));
    
            const totalSubtotal = report.sub_amount || 0;
            const totalWithholdingTax = report.withholding_tax_amount || 0; // Updated field name
            
            form.getTextField('date1').setText(formatDateToDMY(report.approval_date) || '');
            form.getTextField('pay1.0').setText(formatTotalAmount(report.gross_total || 0)); 
            form.getTextField('tax1.0').setText(formatTotalAmount(totalWithholdingTax));
            form.getTextField('total').setText(numberToWordsWithBahtAndSatang(totalWithholdingTax));
    
            form.getCheckBox('chk1').check();
            form.getCheckBox('chk8').check();
    
            const pdfBytes = await pdfDoc.save();
    
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `Tawi50_${report.teacher_name}_${report.period}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating Tawi50 PDF:', error);
        }
    };
    
  const handlePayStubClick = async (report) => {
    try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/teacher-hours/teacher-payment-report/${report.batch_id}/details`, {
            headers: { Authorization: `Bearer ${token}` }
        });

        const payStubData = {
            companyLogo: "/images/keaes.png",
            companyName: "Keaes Academics (Ekamai)",
            companyAddress: "982/22 Gateway Ekamai, 4th Fl. Sukhumvit Rd,",
            companyTax: "TAX ID 0000000000000",
            companyWebsite: "Phra Khanong, Khlong Toei, Bangkok 10110",
            companyPhone: "Tel: 02-108-2922",
            companyLine: "contact@keaes.com",
            
            // Teacher info from the report (main table data)
            teacherName: report.teacher_name,
            teacherAddress: report.address,
            teacherPhone: report.phone,
            teacherNationalId: report.nationalid,
            payPeriod: report.period,
            payDate: report.approval_date,
            
            // Batch-level data from API response
            totalHours: response.data.totalHours,
            totalClasses: response.data.classes.length,
            subAmount: response.data.subAmount,
            grossTotal: response.data.batch.gross_total,
            netTotal: response.data.batch.net_total,
            withholdingTaxAmount: response.data.batch.withholding_tax_amount,
            socialSecurityDeduction: response.data.batch.social_security_deduction,
            otherDeduction: response.data.batch.other_deduction,
            otherDeductionNote: response.data.batch.other_deduction_note,
            otherAddition: response.data.batch.other_addition,
            otherAdditionNote: response.data.batch.other_addition_note,
            withholdingTaxApplied: response.data.classes.some(cls => cls.withholding_tax_applied) || false
        };
        setSelectedPayStub(payStubData);
    } catch (error) {
        console.error(t('errorFetchingClassDetails'), error);
        message.error(t('errorFetchingClassDetails'));
    }
};

    const columns = [
        {
            title: t('approvalDate'),
            dataIndex: 'approval_date',
            key: 'approval_date',
            render: (date) => (
                <div style={{
                    maxWidth: 120,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {formatDateToDMY(date)}
                </div>
            ),
            sorter: (a, b) => new Date(a.approval_date) - new Date(b.approval_date),
        },
        {
            title: t('period'),
            dataIndex: 'period',
            key: 'period',
            render: (periodString) => (
                <div style={{
                    maxWidth: 150,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {formatPeriod(periodString)}
                </div>
            )
        },
        {
            title: t('teacherName'),
            dataIndex: 'teacher_name',
            key: 'teacher_name',
            render: (name) => (
                <div style={{
                    maxWidth: 150,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {name}
                </div>
            ),
        },
        {
            title: t('nationalID'),
            dataIndex: 'nationalid',
            key: 'nationalid',
            render: (id) => (
                <div style={{
                    maxWidth: 130,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {formatNationalId(id)}
                </div>
            ),
        },
        {
            title: t('hours'),
            dataIndex: 'total_hours',
            key: 'total_hours',
            sorter: (a, b) => a.total_hours - b.total_hours,
        },
        {
            title: t('subAmount'),
            dataIndex: 'sub_amount',
            key: 'sub_amount',
            render: (amount) => formatTotalAmount(amount),
        },
         {
                title: t('addition pay'),
                dataIndex: 'other_addition',
                key: 'other_addition',
                render: (amount) => formatTotalAmount(amount),
            },

        {
            title: t('grossTotal'),
            dataIndex: 'gross_total',
            key: 'gross_total',
            render: (amount) => formatTotalAmount(amount),
        },
        {
            title: t('withholdingTax'),
            dataIndex: 'withholding_tax_amount',
            key: 'withholding_tax_amount',
            render: (amount) => formatTotalAmount(amount),
        },
                    {
                title: t('deduction'),
                dataIndex: 'other_deduction',
                key: 'other_deduction',
                render: (amount) => formatTotalAmount(amount),
            },
        {
            title: t('netTotal'),
            dataIndex: 'net_total',
            key: 'net_total',
            render: (amount) => formatTotalAmount(amount),
        },
        {
            title: t('approveby'),
            dataIndex: 'approved_by_nickname', 
            key: 'approved_by_nickname',
            ellipsis: true,
        },          
        {
            title: t('Doc'),
            key: 'paystub',
            render: (_, report) => (
                <Button type="link" icon={<PaperClipOutlined />} onClick={() => handlePayStubClick(report)}>
                </Button>
            ),
        },
        {
            title: t('w/h'),
            key: 'tawi50',
            render: (_, report) => (
                <Button type="link" icon={<FilePdfOutlined />} onClick={() => generateTawi50PDF(report)}>
                </Button>
            ),
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, report) => (
                <Button
                    danger
                    onClick={() => handleDelete(report.batch_id)}
                >
                    {t('Delete')}
                </Button>
            ),
        }
    ];

    return (
        <div style={{ display: 'flex' }}>
            <div style={{ flexGrow: 1 }}>
                {/* Filter Section */}
                <div style={{ marginBottom: '20px', background: '#f5f5f5', padding: '15px', borderRadius: '5px' }}>
                    <Row gutter={[16, 16]} align="bottom">
                        <Col xs={24} sm={8} md={6} lg={5}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px' }}>{t('Teacher')}</label>
                                <Select
                                    placeholder={t('selectTeacher')}
                                    style={{ width: '100%' }}
                                    allowClear
                                    value={selectedTeacher}
                                    onChange={setSelectedTeacher}
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {teachers.map(teacher => (
                                        <Option key={teacher} value={teacher}>{teacher}</Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                        <Col xs={24} sm={10} md={8} lg={7}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px' }}>{t('Period')}</label>
                                <Select
                                    placeholder={t('selectPeriod')}
                                    style={{ width: '100%' }}
                                    allowClear
                                    value={selectedPeriod}
                                    onChange={setSelectedPeriod}
                                >
                                    {periods.map(period => (
                                        <Option key={period} value={period}>{formatPeriod(period)}</Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                        <Col xs={24} sm={6} md={4} lg={3}>
                            <Space>
                                <Button 
                                    icon={<ReloadOutlined />} 
                                    onClick={handleReset}
                                    disabled={!selectedTeacher && !selectedPeriod}
                                >
                                    {t('Reset')}
                                </Button>
                                <Button 
                                    type="primary"
                                    icon={<FilterOutlined />}
                                    onClick={filterReports}
                                >
                                    {t('Filter')}
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                      <div style={{ width: '100%', overflowX: 'auto', marginTop: '24px' }}>
                        <div style={{ minWidth: 1200 }}> 
                            <Table
                            columns={columns}
                            dataSource={filteredReports}
                            rowKey="batch_id"
                            pagination={{ pageSize: 50 }}
                            scroll={{ x: true }} 
                            size="middle"
                            bordered
                            />
                        </div>
                        </div>

                </div>





                <Modal
                    title={t('viewpaystub')}
                    open={selectedPayStub !== null}
                    onCancel={() => setSelectedPayStub(null)}
                    footer={null}
                    width={800}
                >
                    <PayStubModal payStubData={selectedPayStub} onClose={() => setSelectedPayStub(null)} />
                </Modal>
            </div>
        </div>
    );
};

export default withRoleAccess(TeacherPaymentReport, ['superadmin']);